// packages/web/src/api/users/mutations.ts
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  usersUpdate,
  usersSetRole,
  usersLinkErp,
  usersBan,
  usersUnban,
  usersCreate,
  usersInviteCreate,
  usersInviteResend,
} from "../generated/sdk.gen.ts";
import type {
  UsersUpdateData,
  UsersSetRoleData,
  UsersLinkErpData,
  UsersBanData,
  UsersCreateData,
  UsersInviteCreateData,
} from "../generated/types.gen.ts";

type UpdateBody = NonNullable<UsersUpdateData["body"]>;
type SetRoleBody = NonNullable<UsersSetRoleData["body"]>;
type LinkErpBody = NonNullable<UsersLinkErpData["body"]>;
type BanBody = NonNullable<UsersBanData["body"]>;
type CreateBody = NonNullable<UsersCreateData["body"]>;
type InviteCreateBody = NonNullable<UsersInviteCreateData["body"]>;

function invalidateUsers(qc: ReturnType<typeof useQueryClient>) {
  return qc.invalidateQueries({ queryKey: ["users"] });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; body: UpdateBody }) => {
      const { data, error } = await usersUpdate({ path: { id: vars.id }, body: vars.body });
      if (error) throw error;
      return data;
    },
    onSuccess: () => invalidateUsers(qc),
  });
}

export function useSetRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; body: SetRoleBody }) => {
      const { data, error } = await usersSetRole({ path: { id: vars.id }, body: vars.body });
      if (error) throw error;
      return data;
    },
    onSuccess: () => invalidateUsers(qc),
  });
}

export function useLinkErp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; body: LinkErpBody }) => {
      const { data, error } = await usersLinkErp({ path: { id: vars.id }, body: vars.body });
      if (error) throw error;
      return data;
    },
    onSuccess: () => invalidateUsers(qc),
  });
}

export function useBan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; body: BanBody }) => {
      const { data, error } = await usersBan({ path: { id: vars.id }, body: vars.body });
      if (error) throw error;
      return data;
    },
    onSuccess: () => invalidateUsers(qc),
  });
}

export function useUnban() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string }) => {
      const { data, error } = await usersUnban({ path: { id: vars.id } });
      if (error) throw error;
      return data;
    },
    onSuccess: () => invalidateUsers(qc),
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { body: CreateBody }) => {
      const { data, error } = await usersCreate({ body: vars.body });
      if (error) throw error;
      return data;
    },
    onSuccess: () => invalidateUsers(qc),
  });
}

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { body: InviteCreateBody }) => {
      const { data, error } = await usersInviteCreate({ body: vars.body });
      if (error) throw error;
      if (!data) throw new Error("No response data");
      return data;
    },
    onSuccess: () => invalidateUsers(qc),
  });
}

export function useResendInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string }) => {
      const { data, error } = await usersInviteResend({ path: { id: vars.id } });
      if (error) throw error;
      if (!data) throw new Error("No response data");
      return data;
    },
    onSuccess: () => invalidateUsers(qc),
  });
}
